/**
 * engraving-note.js
 *
 * Mirrors the per-line "Engraving" line item properties into the order note
 * (the "Notes" panel shown on the order in the Shopify admin), so engravings
 * are visible there as well as under each product.
 *
 * The cart note is a single field for the whole cart, so engravings are
 * numbered in cart order:
 *
 *   Engravings:
 *   Engraving 1: John
 *   Engraving 2: Stephen
 *
 * The line item properties remain the source of truth — this only derives a
 * readable summary from them. Any text the customer typed into the note is
 * preserved: only the managed "Engravings:" block is rewritten.
 *
 * It re-syncs on page load, whenever the cart updates (theme pubsub /
 * `cart:updated`), and on demand via `window.EngravingNote.sync()` (awaited by
 * the direct-to-checkout flows so the note is saved before the redirect).
 */
(function () {
  if (window.EngravingNote) return;

  var HEADER = 'Engravings:';
  // A property counts as an engraving when its name (case-insensitive) starts
  // with "engraving". Covers the default "Engraving" and variants like
  // "Engraving text". Hidden `_`-prefixed properties are ignored.
  var ENGRAVING_NAME = /^engraving/i;
  // Matches the managed block: the header and its numbered lines.
  var MANAGED_LINE = /^\s*(?:Engravings:\s*|Engraving\s+\d+:.*)$/;

  var inFlight = null;
  var queued = false;

  function collectEngravings(cart) {
    var values = [];
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var props = items[i].properties || {};
      for (var key in props) {
        if (!Object.prototype.hasOwnProperty.call(props, key)) continue;
        if (key.charAt(0) === '_') continue;
        if (!ENGRAVING_NAME.test(key.trim())) continue;
        var value = props[key];
        if (value == null) continue;
        value = String(value).trim();
        if (value) values.push(value);
      }
    }
    return values;
  }

  function stripManagedBlock(note) {
    if (!note) return '';
    var kept = note.split(/\r?\n/).filter(function (line) {
      return !MANAGED_LINE.test(line);
    });
    return kept.join('\n').replace(/\n{3,}/g, '\n\n').replace(/^\s+|\s+$/g, '');
  }

  function buildNote(existingNote, engravings) {
    var base = stripManagedBlock(existingNote);
    if (!engravings.length) return base;

    var block = [HEADER];
    for (var i = 0; i < engravings.length; i++) {
      block.push('Engraving ' + (i + 1) + ': ' + engravings[i]);
    }
    return base ? base + '\n\n' + block.join('\n') : block.join('\n');
  }

  function run() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var current = (cart && cart.note) || '';
        var next = buildNote(current, collectEngravings(cart));
        // Avoid a needless write (and any update -> resync loop).
        if (next === current) return cart;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: next })
        }).then(function (r) { return r.json(); });
      })
      .catch(function () { /* never block the purchase flow on this */ });
  }

  // Serialize calls so overlapping cart events can't write stale notes; if a
  // sync is requested while one is running, run exactly one more afterwards.
  function sync() {
    if (inFlight) {
      queued = true;
      return inFlight;
    }
    inFlight = run().then(function () {
      inFlight = null;
      if (queued) {
        queued = false;
        return sync();
      }
    });
    return inFlight;
  }

  window.EngravingNote = { sync: sync };

  function onReady() {
    sync();

    // Cart mutations from the drawer, cart page, gift wrap, bundles, etc.
    document.addEventListener('cart:updated', sync);
    document.addEventListener('cart:refresh', sync);
    if (window.theme && theme.pubsub && theme.pubsub.subscribe && theme.pubsub.PUB_SUB_EVENTS) {
      theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, sync);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
